/**
 * Hint quality — one question, every session.
 *
 * Two stacked sections, as specified: a sortable/filterable table of the hint
 * sessions on this question, and beneath it the full turn-by-turn detail of
 * whichever session is selected. Selecting a row swaps the lower section; the
 * most recent session is selected on load so the page is never empty.
 *
 * Read-only.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md §7.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api';
import { LatexText, QuestionFigures } from '../latex';
import {
  DataTable, PageHeader, BackButton, LoadingSpinner, ImageViewer, MCQOptionBadge,
  type ColumnDef, type SortState,
} from '../components';
import { buildQuestionDisplayText, buildQuestionCorrectAnswerText, allQuestionFigures } from '@padcanvas/shared';
import { useSubjectLabel } from '../reference-data';
import { createHintQualityApi } from './hintQualityApi';
import { HintTurnCard } from './HintTurnCard';
import type { HintQualitySession } from './types';
import './HintQuality.css';

export interface HintQualityQuestionPageProps {
  questionId: string;
  onBack: () => void;
  /** Session to open on arrival. Set when navigating from the Sessions tab so
   *  the row you clicked is the one detailed, rather than the most recent. */
  initialSessionId?: string;
  /** Label for the back control — the page can be reached from either tab. */
  backLabel?: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

type SessionSortKey = 'startedAt' | 'student' | 'hints' | 'maxLevel' | 'rated';

const LEVEL_ORDER: Record<string, number> = { nudge: 1, direction: 2, guided: 3, reveal: 4 };

function sortValue(s: HintQualitySession, key: SessionSortKey): string | number {
  switch (key) {
    case 'student': return s.student.name.toLowerCase();
    case 'hints': return s.hintsLlm + s.hintsPrewritten;
    case 'maxLevel': return s.maxLevel ? LEVEL_ORDER[s.maxLevel] : 0;
    case 'rated': return s.thumbsDown * 10 + s.thumbsUp;
    default: return s.startedAt;
  }
}

export function HintQualityQuestionPage({
  questionId, onBack, initialSessionId, backLabel = 'Hint Quality',
}: HintQualityQuestionPageProps) {
  const api = useApi();
  const hintQualityApi = useMemo(() => createHintQualityApi(api), [api]);
  const subjectLabel = useSubjectLabel();

  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(initialSessionId ?? null);

  // Refetch rather than patch local state: a thumbs-down also opens a triage
  // thread server-side, so the server's view of the turn is the one to trust.
  const reviewMutation = useMutation({
    mutationFn: (args: { sessionId: string; entryIndex: number; rating: 'positive' | 'negative'; comment?: string }) =>
      hintQualityApi.reviewHint(args),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['hint-quality-question', questionId] }),
  });
  const [sort, setSort] = useState<SortState>({ sortBy: 'startedAt', sortOrder: 'desc' });
  const [onlyRated, setOnlyRated] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['hint-quality-question', questionId],
    queryFn: () => hintQualityApi.getQuestion(questionId),
  });

  const sessions = useMemo(() => {
    const all = data?.sessions || [];
    const filtered = onlyRated ? all.filter(s => s.thumbsUp + s.thumbsDown > 0) : all;
    const key = sort.sortBy as SessionSortKey;
    const sign = sort.sortOrder === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortValue(a, key), bv = sortValue(b, key);
      return av === bv ? 0 : (av < bv ? -sign : sign);
    });
  }, [data?.sessions, sort, onlyRated]);

  // Keep a valid selection as filters/sorting change: default to the first row,
  // and re-point if the selected session is filtered out. An `initialSessionId`
  // that isn't in this question's sessions (stale link, or a session the caller
  // may not see) falls back to the first row rather than showing nothing.
  useEffect(() => {
    if (sessions.length === 0) { setSelectedId(null); return; }
    if (!selectedId || !sessions.some(s => s.id === selectedId)) {
      setSelectedId(sessions[0].id);
    }
  }, [sessions, selectedId]);

  // Bring the deep-linked session into view — arriving from the Sessions tab,
  // the row of interest can otherwise be below the fold. Targets the selected
  // row by its class (DataTable has no per-row hook), after the rows render.
  useEffect(() => {
    if (!initialSessionId || sessions.length === 0) return;
    const id = requestAnimationFrame(() => {
      document.querySelector('.hintq-row--selected')
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [initialSessionId, sessions.length]);

  const selected = sessions.find(s => s.id === selectedId);

  const columns = useMemo<ColumnDef<HintQualitySession>[]>(() => [
    {
      key: 'student',
      header: 'Student',
      sortable: true,
      render: s => (
        <div className="hintq-stack">
          <strong>{s.student.name}</strong>
          {s.student.grade ? <span className="hintq-sub">Grade {s.student.grade}</span> : null}
        </div>
      ),
    },
    {
      key: 'startedAt',
      header: 'When',
      sortable: true,
      render: s => <span className="hintq-sub">{formatDate(s.startedAt)}</span>,
    },
    {
      key: 'hints',
      header: 'Hints',
      sortable: true,
      className: 'hintq-num',
      render: s => (
        <div className="hintq-stack">
          <strong>{s.hintsLlm + s.hintsPrewritten}</strong>
          {s.hintsPrewritten > 0 && (
            <span className="hintq-sub">{s.hintsLlm} AI · {s.hintsPrewritten} ready</span>
          )}
        </div>
      ),
    },
    {
      key: 'maxLevel',
      header: 'Depth',
      sortable: true,
      render: s => s.maxLevel
        ? <span className={`hintq-badge hintq-level--${s.maxLevel}`}>{s.maxLevel}</span>
        : <span className="hintq-muted">—</span>,
    },
    {
      key: 'context',
      header: 'Context',
      render: s => (
        <span className="hintq-sub">
          {s.testSessionId ? 'Assignment' : 'Practice'}{s.inputMode ? ` · ${s.inputMode}` : ''}
        </span>
      ),
    },
    {
      key: 'rated',
      header: 'Rated',
      sortable: true,
      className: 'hintq-num',
      render: s => (s.thumbsUp + s.thumbsDown === 0
        ? <span className="hintq-muted">—</span>
        : (
          <div className="hintq-ratings">
            {s.thumbsUp > 0 && <span className="hintq-rating hintq-rating--up">👍 {s.thumbsUp}</span>}
            {s.thumbsDown > 0 && <span className="hintq-rating hintq-rating--down">👎 {s.thumbsDown}</span>}
          </div>
        )),
    },
  ], []);

  if (isLoading) return <div className="hintq-page"><LoadingSpinner /></div>;
  if (error) {
    return <div className="hintq-page"><div className="hintq-error">Couldn't load this question: {(error as Error).message}</div></div>;
  }

  const q = data?.question;
  const ratedCount = (data?.sessions || []).filter(s => s.thumbsUp + s.thumbsDown > 0).length;

  return (
    <div className="hintq-page">
      <BackButton onClick={onBack} label={backLabel} />

      <PageHeader
        title="Hint Quality"
        subtitle={q ? `${q.subject ? subjectLabel(q.subject) : ''}${q.topic ? ` · ${q.topic}` : ''}` : undefined}
      />

      <div className="hintq-question card">
        {/* The whole question, not just the stem — an MCQ stem without its
            options can't be judged, and STRUCTURED parts live off the stem. */}
        {q
          ? <LatexText text={buildQuestionDisplayText(q) || q.questionText} figures={allQuestionFigures(q)} />
          : <span className="hintq-muted">Question not found.</span>}
        {q?.questionDiagramUrl && (
          <ImageViewer src={q.questionDiagramUrl} alt="Question diagram" className="hintq-qdiagram" />
        )}
        {q && <QuestionFigures text={q.questionText} figures={q.figures} className="hintq-qdiagram" />}
        {!!q?.options?.length && (
          <div className="hintq-options">
            {q.options.map(o => (
              <MCQOptionBadge
                key={o.label}
                option={{ label: o.label, text: o.text, imageUrl: o.imageUrl }}
                variant={o.isCorrect ? 'correct' : 'neutral'}
              />
            ))}
          </div>
        )}
        {q && !q.options?.length && buildQuestionCorrectAnswerText(q) && (
          <p className="hintq-answer">
            <span className="hintq-section-title">Correct answer</span>
            <LatexText text={buildQuestionCorrectAnswerText(q)} />
          </p>
        )}
        {!!q?.hints?.length && (
          <div className="hintq-prewritten-list">
            <h5 className="hintq-section-title">Ready-made hints on this question</h5>
            <ol>{q.hints.map((h, i) => <li key={i}><LatexText text={h} /></li>)}</ol>
          </div>
        )}
      </div>

      <section className="hintq-sessions">
        <div className="hintq-sessions-head">
          <h3>Hint sessions ({sessions.length})</h3>
          {ratedCount > 0 && (
            <label className="hintq-toggle">
              <input
                type="checkbox"
                checked={onlyRated}
                onChange={e => setOnlyRated(e.target.checked)}
              />
              Only rated ({ratedCount})
            </label>
          )}
        </div>
        <DataTable
          columns={columns}
          data={sessions}
          rowKey={s => s.id}
          onRowClick={s => setSelectedId(s.id)}
          rowClassName={s => (s.id === selectedId ? 'hintq-row--selected' : '')}
          sort={sort}
          onSortChange={setSort}
          compact
          emptyMessage={
            data?.scope === 'students'
              ? 'None of your students have used hints on this question.'
              : 'No hint sessions on this question yet.'
          }
        />
      </section>

      <section className="hintq-detail">
        {selected ? (
          <>
            <div className="hintq-detail-head">
              <h3>{selected.student.name}</h3>
              <span className="hintq-sub">
                {formatDate(selected.startedAt)} · {selected.timeline.length} hint
                {selected.timeline.length === 1 ? '' : 's'}
                {selected.testSessionId ? ' · assignment' : ' · practice'}
              </span>
            </div>
            <div className="hintq-timeline">
              {selected.timeline.map((turn, i) => (
                <HintTurnCard
                  key={`${selected.id}-${i}`}
                  turn={turn}
                  studentName={selected.student.name}
                  onReview={(rating, comment) => reviewMutation.mutateAsync({
                    sessionId: selected.id,
                    entryIndex: turn.entryIndex,
                    rating,
                    comment,
                  }).then(() => undefined)}
                />
              ))}
            </div>
          </>
        ) : (
          <p className="hintq-muted">Select a session above to see its hints.</p>
        )}
      </section>
    </div>
  );
}
